import React, { useState, useEffect, useCallback } from 'react';

interface WatermarkProps {
  apiUrl: string;
}

type Mode = 'page' | 'images';

interface Rule {
  pattern: string;
  mode: Mode;
  enabled: boolean;
  opacity?: number;
  density: 'normal' | 'dense';
}

const Watermark: React.FC<WatermarkProps> = ({ apiUrl }) => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [pattern, setPattern] = useState('');
  const [mode, setMode] = useState<Mode>('page');
  const [opacity, setOpacity] = useState<number>(0);
  const [density, setDensity] = useState<'normal' | 'dense'>('normal');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/admin/bdr/watermark-rules`).then((x) => x.json());
      setRules(Array.isArray(r.rules) ? r.rules : []);
    } catch (err) {
      console.error('Failed to load watermark rules:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { load(); }, [load]);

  const add = () => {
    const p = pattern.trim();
    if (!p) return;
    setRules((rs) => [...rs, { pattern: p, mode, enabled: true, ...(opacity ? { opacity } : {}), density }]);
    setPattern('');
  };

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const res = await fetch(`${apiUrl}/admin/bdr/watermark-rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.message || 'Save failed');
      setRules(body.rules);
      setNotice('Saved. Endpoint agents pick this up within seconds.');
    } catch (err: any) {
      setNotice(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page-content"><p>Loading…</p></div>;

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>Watermarking</h1>
          <p className="subtitle">Mark pages or images from selected sites with the viewer's name, device and time.</p>
        </div>
      </div>

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">Add a rule</div>
        <div className="ent-card-body">
          <div className="ent-toolbar" style={{ marginTop: 0, gap: 8 }}>
            <input
              className="ent-input"
              style={{ minWidth: 280 }}
              placeholder="docs.corp.com  or  portal.corp.com/reports"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            />
            <select className="ent-select" value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
              <option value="page">Whole page view</option>
              <option value="images">Images only</option>
            </select>
            <select className="ent-select" value={opacity} onChange={(e) => setOpacity(Number(e.target.value))}>
              <option value={0}>Default strength</option>
              <option value={10}>Subtle (10%)</option>
              <option value={20}>Medium (20%)</option>
              <option value={35}>Strong (35%)</option>
              <option value={50}>Very strong (50%)</option>
            </select>
            <select className="ent-select" value={density} onChange={(e) => setDensity(e.target.value as 'normal' | 'dense')}>
              <option value="normal">Normal spacing</option>
              <option value="dense">Dense spacing</option>
            </select>
            <button className="ent-btn" onClick={add}>Add</button>
          </div>
          <p style={{ color: 'var(--text-2)', fontSize: 12, marginBottom: 0 }}>
            A host (<code>docs.corp.com</code>, also matches subdomains) optionally followed by a path prefix.
            "Whole page view" overlays the rendered page; "Images only" burns the mark into PNG/JPEG files served from the matching URL.
          </p>
        </div>
      </div>

      <div className="ent-card">
        <div className="ent-card-head">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>Rules ({rules.length})</span>
            <button className="ent-btn primary sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
        <div className="ent-card-body">
          {notice && <p style={{ marginTop: 0 }}>{notice}</p>}
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Enabled</th>
                  <th>URL pattern</th>
                  <th style={{ width: 180 }}>Watermark</th>
                  <th style={{ width: 150 }}>Strength / spacing</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 && (
                  <tr><td colSpan={5} className="ent-empty">No rules. Nothing is watermarked.</td></tr>
                )}
                {rules.map((r, i) => (
                  <tr key={`${r.pattern}-${r.mode}-${i}`}>
                    <td>
                      <input
                        type="checkbox"
                        checked={r.enabled}
                        onChange={() => setRules((rs) => rs.map((x, j) => (j === i ? { ...x, enabled: !x.enabled } : x)))}
                      />
                    </td>
                    <td><code>{r.pattern}</code></td>
                    <td>{r.mode === 'page' ? 'Whole page view' : 'Images only'}</td>
                    <td>{r.opacity ? `${r.opacity}%` : 'default'} / {r.density}</td>
                    <td><button className="ent-btn sm" onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ color: 'var(--text-2)', fontSize: 12, marginBottom: 0 }}>
            This is a deterrent and a leak-tracing aid, not prevention: a user with browser developer tools can remove the page overlay,
            and traffic from a browser that is not using the Agenti5 proxy is not marked.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Watermark;
